import { parseOAuthResult, type OAuthConnectResult } from './oauthResult';

/**
 * sessionStorage key holding the in-app route the connect was started from.
 *
 * PostForMe redirects to the ONE `redirect_uri` registered with it (`/app?…`),
 * not back to the page we left, so we remember the route ourselves. Session
 * (not local) storage because it is per-tab and survives the full-page round
 * trip out to the provider and back.
 */
const RETURN_ROUTE_KEY = 'social-oauth:return-route';

/**
 * The connect result the provider put on the URL, read once at boot. It has to
 * be captured that early: React Router rewrites `/app` → `/app/dashboard` and
 * drops the query string before any component effect could read it.
 */
let capturedResult: OAuthConnectResult | null = null;

/**
 * Hand the whole tab to the provider's authorization page.
 *
 * Deliberately NOT a popup. A popup has to relay its result back across a
 * cross-origin boundary, and every signal available for that is unreliable:
 * browsers clear `window.name` on cross-site navigation, COOP can sever
 * `window.opener`, and popup blockers stop the flow outright — which is why the
 * old implementation needed four delivery channels and still dropped results.
 * On top of that `window.open` REUSES any window already carrying the same
 * name, so connecting a second platform navigated the first platform's popup
 * instead of opening its own (LIL-490).
 *
 * A top-level redirect has none of those failure modes: the provider returns to
 * our origin as an ordinary navigation and the app reads the result off the URL.
 */
export function startSocialOAuth(authUrl: string, returnRoute: string): void {
  try {
    sessionStorage.setItem(RETURN_ROUTE_KEY, returnRoute);
  } catch {
    // Non-fatal: we just land on the redirect's own route instead.
  }
  window.location.assign(authUrl);
}

/**
 * Read the provider's result off the current URL and hold it until the app has
 * mounted. Call before React renders — see `capturedResult`.
 */
export function captureOAuthRedirectResult(search: string = window.location.search): void {
  capturedResult = parseOAuthResult(search);
}

/**
 * Take the captured result, clearing it so it is acted on exactly once. A
 * second read must come back empty: StrictMode remounts effects in dev, and
 * re-toasting the same failure would be wrong.
 */
export function takeOAuthRedirectResult(): OAuthConnectResult | null {
  const result = capturedResult;
  capturedResult = null;
  return result;
}

/**
 * Take the route the connect was started from, clearing it so a later visit
 * isn't hijacked by a stale one. Null when we never stored a route — the
 * provider's fixed `redirect_uri` can land on a different origin than the user
 * started on — so callers need a fallback.
 */
export function takeOAuthReturnRoute(): string | null {
  try {
    const route = sessionStorage.getItem(RETURN_ROUTE_KEY);
    sessionStorage.removeItem(RETURN_ROUTE_KEY);
    return route;
  } catch {
    return null;
  }
}
